/**
 * AZ EJTŐMEZŐ — a csoportos feltöltés asztali belépési pontja.
 *
 * A makett (`design/UgyfelAsztal.dc.html`) jobb oldali paneljének megfelelője:
 * „Húzd ide a fájlokat", alatta a magyarázat, majd „vagy" és egy gomb. A gomb
 * NEM dísz: az ejtés egérre való, a billentyűzetnek és a képernyőolvasónak a
 * fájlválasztó AZ út. Ezért nincs `tabindex` és `role` magán a mezőn — egy
 * fókuszálható `div`, ami semmit nem csinál `Enter`-re, rosszabb a hiányánál.
 *
 * MOBILON NEM LÁTSZIK. Nem `matchMedia`-val döntjük el, hanem CSS-ben
 * (`.dropzone` a 44rem töréspont alatt `display: none`): a drag-and-drop
 * érintőképernyőn nem létező művelet, és egy felirat, ami olyat kér, amit a
 * készülék nem tud, csak zavar. A lábsáv gombja mobilon is ott van.
 *
 * ── HÁROM DOLOG, AMI NÉLKÜL EZ CSENDESEN ROSSZ ───────────────────────
 *
 * 1. A LAPRA EJTÉS AZ EJTŐMEZŐN KÍVÜL. Alapértelmezésben a böngésző MEGNYITJA
 *    az ejtett fájlt — elnavigál az appból, és a félig kitöltött állapot
 *    elvész. Ezért az ablakon is fogjuk a `dragover`/`drop` eseményt. A mi
 *    kezelőnk hamarabb fut (a mezőn buborékol felfelé), tehát ez nem viszi el
 *    a valódi ejtést.
 *
 * 2. A `dragleave` A GYEREKELEMEKEN IS TÜZEL. Egy sima be/ki jelző ezért
 *    villog, amikor az egér a felirat fölé ér. A számláló azt méri, hány
 *    elemben vagyunk bent — nulla az igazi kilépés.
 *
 * 3. A MAPPA NEM FÁJL. Mappát ejteni természetes mozdulat, és a `2.4` válasza
 *    óta az asztali használat elsőrendű. Mappa-bejárást ez a kör nem épít
 *    (a hatókör döntése), de a NÉMA elnyelés a legrosszabb kimenet: az ügyfél
 *    azt hinné, feltöltötte. Ezért felismerjük és KIMONDJUK.
 */

import { type DragEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadIcon } from '@/components/common';
import { FilePicker } from './FilePicker';

interface DropZoneProps {
  disabled?: boolean;
  onPick: (files: File[]) => void;
}

/**
 * Mit ejtettek — fájlokat, mappát, vagy mindkettőt.
 *
 * A `webkitGetAsEntry()` SZINKRON HÍVÁS, és csak az esemény alatt él: a
 * `DataTransferItemList` a kezelő visszatérése után használhatatlan. Ezért
 * fut minden kicsomagolás itt, `await` nélkül.
 */
function unpack(transfer: DataTransfer): { files: File[]; sawFolder: boolean } {
  const items = Array.from(transfer.items ?? []);

  // Ha az `items` nem elérhető, a `files` a tartalék. Mappát ilyenkor nem
  // tudunk megkülönböztetni — a szerver adná vissza hibával.
  if (items.length === 0) {
    return { files: Array.from(transfer.files ?? []), sawFolder: false };
  }

  const files: File[] = [];
  let sawFolder = false;

  for (const item of items) {
    if (item.kind !== 'file') {
      continue;
    }
    // OPCIONÁLIS HÍVÁS: ahol a `webkitGetAsEntry` nincs, a hívás DOBNA, és az
    // ejtésre nem történne SEMMI — pont az a néma nulla, ami ellen három
    // sorral fentebb a tartalék-ág íródott. Így legfeljebb a mappát nem
    // ismerjük fel helyben; azt a szerver utasítja vissza.
    if (item.webkitGetAsEntry?.()?.isDirectory) {
      sawFolder = true;
      continue;
    }
    const file = item.getAsFile();
    if (file) {
      files.push(file);
    }
  }

  return { files, sawFolder };
}

export function DropZone({ disabled = false, onPick }: DropZoneProps) {
  const { t } = useTranslation();
  const [over, setOver] = useState(false);
  const [folderRefused, setFolderRefused] = useState(false);
  /** Ejtés érkezett, miközben egy feltöltés még futott. */
  const [busyRefused, setBusyRefused] = useState(false);
  /** Lásd a fejléc 2. pontját: a `dragleave` a gyerekeken is tüzel. */
  const depth = useRef(0);

  useEffect(() => {
    // Lásd a fejléc 1. pontját.
    const swallow = (event: Event) => event.preventDefault();
    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallow);
    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallow);
    };
  }, []);

  const leave = useCallback(() => {
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) {
      setOver(false);
    }
  }, []);

  const drop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      depth.current = 0;
      setOver(false);

      // ELŐBB TAKARÍTUNK. Egy korábbi ejtés mappa-üzenete különben ottmaradna
      // egy teljesen más ejtés mellett, és a felhasználó arra vonatkoztatná.
      setFolderRefused(false);

      if (disabled) {
        // NEM NÉMÁN. Egy elnyelt ejtés úgy néz ki, mintha elfogadtuk volna —
        // az ügyfél elmenne a képernyőről abban a hitben, hogy elküldte.
        setBusyRefused(true);
        return;
      }
      setBusyRefused(false);

      const { files, sawFolder } = unpack(event.dataTransfer);

      // A MAPPÁT AKKOR IS KIMONDJUK, HA MELLETTE JÖTT HASZNÁLHATÓ FÁJL. Egy
      // vegyes ejtésnél („a mappa és két külön számla") a néma elnyelés még
      // rosszabb, mint egyedül: a nyugta megjelenne, tehát az ügyfél azt
      // hinné, MINDEN átment.
      setFolderRefused(sawFolder);

      if (files.length > 0) {
        onPick(files);
      }
    },
    [disabled, onPick]
  );

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: az ejtés egérre való, a billentyűzet útja a mezőben lévő fájlválasztó gomb — lásd a fejlécet
    <div
      className={`dropzone${over ? ' dropzone-over' : ''}`}
      onDragEnter={(event) => {
        event.preventDefault();
        depth.current += 1;
        setOver(true);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={leave}
      onDrop={drop}
    >
      <span className="dropzone-icon" aria-hidden="true">
        <UploadIcon />
      </span>
      <p className="dropzone-title">{t('upload.dropHere')}</p>
      <p className="meta">{t('upload.dropHint')}</p>
      <p className="meta dropzone-or">{t('upload.or')}</p>

      <FilePicker
        label={t('upload.chooseFromComputer')}
        variant="secondary"
        multiple
        disabled={disabled}
        onPick={(files) => {
          setFolderRefused(false);
          setBusyRefused(false);
          onPick(files);
        }}
      />

      {folderRefused ? (
        <p className="notice error" role="alert">
          {t('upload.folderNotSupported')}
        </p>
      ) : null}

      {busyRefused ? (
        <p className="notice error" role="alert">
          {t('upload.stillRunning')}
        </p>
      ) : null}
    </div>
  );
}
